import {useCallback, useState} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import {isAllRepositoriesSelection, isNoRepoSelection} from '@github-ui/repos-selector'

const REPO_FILTER_PARAM = 'repo_filter'

/** Sentinel value for the repo filter indicating tasks with no associated repository. */
export const NO_REPO_FILTER = 'none'
/** Sentinel value for the repo filter indicating tasks associated with any repository. */
export const ALL_REPOSITORIES_FILTER = 'repositories'

interface RepoTasksFilter {
  /** Current filter: `undefined` (all sessions), a special filter value, or a NWO string. */
  repoFilter: string | undefined
  /** Numeric repo ID, if a specific repo is selected. */
  repoId: number | undefined
  /** Update the filter from a ReposSelector selection. */
  setRepoFilter: (repo: RepoTasksSelection | undefined) => void
}

type RepoTasksSelection = {nameWithOwner: string; id?: number} | {_noRepo: true} | {_allRepositories: true}

/**
 * Manages repository filter state for the agents page.
 * NWO is stored in URL params (bookmarkable), repo ID is kept in React state.
 */
export function useRepoTasksFilter(): RepoTasksFilter {
  const [searchParams, setSearchParams] = useSearchParams()
  const [repoId, setRepoId] = useState<number | undefined>()

  const repoFilterParam = searchParams.get(REPO_FILTER_PARAM)
  const repoFilter = repoFilterParam || undefined

  const setRepoFilter = useCallback(
    (repo: RepoTasksSelection | undefined) => {
      const nextRepoFilter =
        repo && isNoRepoSelection(repo)
          ? NO_REPO_FILTER
          : repo && isAllRepositoriesSelection(repo)
            ? ALL_REPOSITORIES_FILTER
            : repo && 'nameWithOwner' in repo
              ? repo.nameWithOwner
              : undefined
      const nextRepoId =
        nextRepoFilter && nextRepoFilter !== NO_REPO_FILTER && nextRepoFilter !== ALL_REPOSITORIES_FILTER
          ? repo && 'id' in repo
            ? repo.id
            : undefined
          : undefined

      setRepoId(nextRepoId)
      if (nextRepoFilter === repoFilter) return

      const nextSearchParams = new URLSearchParams(searchParams)
      if (nextRepoFilter) {
        nextSearchParams.set(REPO_FILTER_PARAM, nextRepoFilter)
      } else {
        nextSearchParams.delete(REPO_FILTER_PARAM)
      }
      nextSearchParams.delete('page')
      setSearchParams(nextSearchParams)
    },
    [repoFilter, searchParams, setSearchParams],
  )

  return {repoFilter, repoId, setRepoFilter}
}
